import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/core";
import update from "immutability-helper";
import { get, isEmpty, map, sortBy, size, filter } from "lodash";

import Box from "@material-ui/core/Box";

import FormSection from "../../Common/FormSection";
import CustomButton from "../../Common/CustomButton";
import DatePicker from "../../Common/DatePicker";
import SitesFilter from "../SitesFilter";

import { getLov } from "../../../utils/lov";
import { nowWithOutSeconds } from "../../../utils/date";

const availablePeriods = [
  {
    name: "today",
    daysNumber: "0",
  },
  {
    name: "yesterday",
    daysNumber: 1,
  },
  {
    name: "last2days",
    daysNumber: 2,
  },
  {
    name: "last7days",
    daysNumber: 7,
  },
  {
    name: "custom",
    daysNumber: "custom",
  },
];

const styles = {
  gridLine: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, 200px)",
    gridAutoRows: "56px",
    gridGap: "24px",
    padding: "24px",
  },
};

const HspForm = (props) => {
  const { classes, search, language } = props;
  const [filters, setFilters] = React.useState({
    tags: null,
    dateFilter: null,
    startDate: null,
    endDate: null,
  });
  const [tags, setTags] = React.useState();

  React.useEffect(() => {
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set initial filters
  React.useEffect(() => {
    if (!isEmpty(tags)) {
      const now = new Date();
      const midnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0
      );
      setFilters(
        update(filters, {
          tags: { $set: map(tags, (t) => t.id) },
          dateFilter: { $set: "0" },
          startDate: { $set: midnight.getTime() },
          endDate: { $set: midnight.getTime() + 86400000 },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const fetchLov = async () => {
    try {
      const tags = await getLov("tags");
      if (size(filter(tags, (i) => i.id === -1)) === 0) {
        tags.push({
          id: -1,
          name_en: I18n.get("site_noTag"),
          name_fr: I18n.get("site_noTag"),
        });
      }
      setTags(tags);
    } catch (e) {
      console.log(e);
    }
  };

  const handleChange = (id, index, value) => {
    if (id === "period") {
      if (value === "custom") {
        setFilters(
          update(filters, {
            dateFilter: { $set: "custom" },
            startDate: { $set: nowWithOutSeconds() - 86400000 },
            endDate: { $set: nowWithOutSeconds() },
          })
        );
      } else {
        const now = new Date();
        const midnight = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          0,
          0
        );
        const startDate =
          value === "0"
            ? midnight.getTime()
            : new Date(midnight - 86400000 * value).getTime();
        const endDate =
          value === "0" ? midnight.getTime() + 86400000 : midnight.getTime();

        setFilters(
          update(filters, {
            dateFilter: { $set: value },
            startDate: { $set: startDate },
            endDate: { $set: endDate },
          })
        );
      }
    } else {
      setFilters(
        update(filters, {
          [id]: { $set: value },
        })
      );
    }
  };

  const handleChangeDate = (id, value) => {
    setFilters(
      update(filters, {
        [id]: { $set: new Date(value).getTime() },
      })
    );
  };

  return (
    <Box className={classes.gridLine}>
      <SitesFilter
        formValue={get(filters, ["sites"]) || []}
        setFormValue={handleChange}
        defaultSelectAll={true}
      />
      <Box>
        <FormSection
          formValue={get(filters, ["tags"]) || []}
          setFormValue={handleChange}
          title2={I18n.get("sidebar_tags")}
          id={"tags"}
          type="select"
          options={{
            selectAll: true,
            multipleSelect: true,
            clickThrough: true,
            options: map(
              sortBy(tags, (tag) => get(tag, [`name_${language}`])),
              (tag) => ({
                id: tag.id,
                name: get(tag, [`name_${language}`]),
              })
            ),
          }}
        />
      </Box>
      <Box>
        <FormSection
          formValue={[get(filters, ["dateFilter"])]}
          setFormValue={handleChange}
          title={"period"}
          id={"period"}
          type="select"
          options={{
            noSort: true,
            options: map(availablePeriods, (p) => ({
              id: p.daysNumber,
              name: I18n.get(`dashboard_${p.name}`),
            })),
          }}
        />
      </Box>
      {get(filters, ["dateFilter"]) === "custom" && (
        <>
          <Box>
            <DatePicker
              label={I18n.get("site_startDate")}
              name="startDate"
              value={get(filters, ["startDate"])}
              onChange={handleChangeDate}
              width={200}
            />
          </Box>
          <Box>
            <DatePicker
              label={I18n.get("site_endDate")}
              name="endDate"
              value={get(filters, ["endDate"])}
              onChange={handleChangeDate}
              width={200}
            />
          </Box>
        </>
      )}
      <Box style={{ display: "flex", alignItems: "flex-end" }}>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={() => search(filters)}
        >
          {I18n.get("dashboard_search")}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default withStyles(styles)(HspForm);
